import React, { useMemo } from 'react';
import { SxProps, Theme, TypographyProps } from '@mui/material';
import { Modal } from 'components/Modal/Modal';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useLayoutElementsHeight } from 'hooks/useLayoutElementsHeight';

export interface DetailModalProps {
  /** Controls whether the modal is visible. */
  open: boolean;
  /** Dismiss callback. */
  onClose: () => void;
  /** Header title; shown on mobile/tablet, hidden on desktop. */
  title: string;
  /** id linking the header title to the modal for accessibility. */
  titleId: string;
  /** When a nested dialog is open, hide the modal backdrop. */
  isDialogOpen?: boolean;
  /** Modal body: detail content followed by any nested confirmation dialog. */
  children: React.ReactNode;
}

/**
 * Responsive modal shell shared by the resource detail pages
 * (care program, benefit). Owns the device-specific header styles,
 * top offset, and slot positioning so each detail page only supplies
 * its own body content.
 */
export const DetailModal: React.FC<DetailModalProps> = ({
  open,
  onClose,
  title,
  titleId,
  isDialogOpen = false,
  children,
}) => {
  const { isMobile, isDesktop } = useDeviceType();
  const { pageHeaderHeight, appBarHeight } = useLayoutElementsHeight();
  const totalHeaderHeight = appBarHeight + pageHeaderHeight;

  let topOffset = appBarHeight; // tablet default
  if (isDesktop) {
    topOffset = totalHeaderHeight;
  } else if (isMobile) {
    topOffset = 0;
  }

  const headerStyles = useMemo(() => {
    if (isMobile) {
      return undefined;
    }
    let headerProps: SxProps<Theme> = { boxShadow: 'none' };

    if (!isDesktop) {
      headerProps = {
        borderTop: 1,
        borderBottom: 1,
        borderColor: 'divider',
        ...headerProps,
      };
    }
    return headerProps;
  }, [isDesktop, isMobile]);

  // Keep the title in the a11y tree on all sizes; hide it visually on desktop
  // so the modal/heading keep an accessible name without showing a header title.
  const modalTitleProps = useMemo<TypographyProps>(
    () => (isDesktop ? { sx: { opacity: 0 } } : { sx: { textAlign: 'left' } }),
    [isDesktop],
  );

  const modalStyles = {
    height: '100%',
    overflowY: 'hidden',
    ...(!isDesktop && !isMobile && { boxShadow: 'none' }), // tablet only
  };

  const slotProps = {
    root: {
      style: {
        top: topOffset,
      },
    },
    backdrop: {
      style: {
        top: topOffset,
      },
    },
  };

  return (
    <Modal
      open={open}
      headerStyles={headerStyles}
      modalTitleProps={modalTitleProps}
      onClose={onClose}
      showHeader
      modalStyles={modalStyles}
      slotProps={slotProps}
      modalTitle={title}
      modalTitleId={titleId}
      maxWidth="sm"
      fullWidth
      // On desktop keep the backdrop so it darkens the page but not the modal panel.
      hideBackdrop={isDialogOpen && !isDesktop}
    >
      {children}
    </Modal>
  );
};
